/* eslint-disable react-refresh/only-export-components */
import React, { useState, useEffect } from 'react';
import { X, Plus, Bookmark, Search } from 'lucide-react';
import { libraryApi, blogApi, sitesApi } from '../../api/client';
import type { ComponentType, LibraryComponent } from '../../schemas/validation';
import { CompeteFormPicker } from '../CompeteForm/CompeteFormPicker';
import type { StudioCaptureForm } from '../CompeteForm/types';
import { cn } from '../../utils/cn';
import { componentOptions, LAYOUT_MODE_COMPONENTS, type ComponentOption } from './componentOptions';
import { TemplatesTab } from './TemplatesTab';
import { LibraryTab } from './LibraryTab';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';

interface ComponentLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (componentType: ComponentType) => void;
  onPlaceCompeteForm?: (form: StudioCaptureForm) => void;
  onSelectLibraryComponent: (libraryComponentId: string) => void;
  onEditLibraryComponent?: (libraryComponent: LibraryComponent) => void;
  isLoading?: boolean;
  siteId?: string;
  refreshTrigger?: number;
  allowedCategories?: string[];
  existingComponentTypes?: ComponentType[];
  isLayoutMode?: boolean;
}

export const ComponentLibraryModal: React.FC<ComponentLibraryModalProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
  onPlaceCompeteForm,
  onSelectLibraryComponent,
  onEditLibraryComponent,
  isLoading = false,
  siteId,
  refreshTrigger,
  allowedCategories,
  existingComponentTypes = [],
  isLayoutMode = false,
}) => {
  const [activeTab, setActiveTab] = useState<'templates' | 'library'>('templates');
  const [libraryComponents, setLibraryComponents] = useState<LibraryComponent[]>([]);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<ComponentType | 'all'>('all');
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    component: LibraryComponent | null;
  }>({ isOpen: false, component: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [siteHasBlog, setSiteHasBlog] = useState(false);
  const [studioLinked, setStudioLinked] = useState(false);
  const [studioForms, setStudioForms] = useState<StudioCaptureForm[]>([]);
  const [studioFormsLoading, setStudioFormsLoading] = useState(false);
  const [pickingCompeteForm, setPickingCompeteForm] = useState(false);

  const getCurrentUserId = (): string | null => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return null;
      const user = JSON.parse(userStr);
      return user.id || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (isOpen && activeTab === 'library') {
      loadLibraryComponents();
    }
  }, [isOpen, activeTab, siteId, refreshTrigger]);

  useEffect(() => {
    if (isOpen && siteId) {
      blogApi
        .get(siteId)
        .then(() => setSiteHasBlog(true))
        .catch(() => setSiteHasBlog(false));
    } else {
      setSiteHasBlog(false);
    }
  }, [isOpen, siteId]);

  useEffect(() => {
    if (!isOpen) {
      setPickingCompeteForm(false);
      return;
    }
    if (!siteId || isLayoutMode) {
      setStudioLinked(false);
      setStudioForms([]);
      return;
    }

    let cancelled = false;
    setStudioFormsLoading(true);
    sitesApi
      .getStudioCaptureForms(siteId)
      .then(result => {
        if (cancelled) return;
        setStudioLinked(result.linked);
        setStudioForms(result.forms);
      })
      .catch(() => {
        if (cancelled) return;
        setStudioLinked(false);
        setStudioForms([]);
      })
      .finally(() => {
        if (!cancelled) setStudioFormsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, siteId, isLayoutMode]);

  const loadLibraryComponents = async () => {
    try {
      setIsLoadingLibrary(true);
      const response = await libraryApi.browseComponents();
      setLibraryComponents(response.components);
    } catch (error) {
      console.error('Failed to load library components:', error);
      setLibraryComponents([]);
    } finally {
      setIsLoadingLibrary(false);
    }
  };

  // Filter library components
  const filteredLibraryComponents = libraryComponents.filter(component => {
    if (isLayoutMode && !LAYOUT_MODE_COMPONENTS.includes(component.type)) {
      return false;
    }

    const matchesSearch =
      !searchTerm ||
      component.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      component.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      component.tags?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = selectedType === 'all' || component.type === selectedType;
    const notAlreadyAdded = !existingComponentTypes.includes(component.type);

    return matchesSearch && matchesType && notAlreadyAdded;
  });

  // Technical components are only shown via the Page Settings banner (InvisibleComponentsBar)
  const TECHNICAL_COMPONENT_TYPES: ComponentType[] = ['head-content', 'body-end'];

  // Filter template components
  const filteredTemplateComponents = componentOptions.filter(component => {
    if (isLayoutMode && !LAYOUT_MODE_COMPONENTS.includes(component.type)) {
      return false;
    }

    if (!isLayoutMode && component.type === 'content-area') {
      return false;
    }

    // Blog Preview only when site has a blog
    if (component.requiresBlog && !siteHasBlog) {
      return false;
    }

    if (component.requiresStudioLink && !studioLinked) {
      return false;
    }

    // Hide technical components from the regular library - they're accessed via Page Settings banner
    // Only show them when explicitly allowed (invisible component mode)
    if (
      TECHNICAL_COMPONENT_TYPES.includes(component.type) &&
      (!allowedCategories || !allowedCategories.includes('Technical'))
    ) {
      return false;
    }

    const matchesSearch =
      !searchTerm ||
      component.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      component.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = selectedType === 'all' || component.type === selectedType;

    const matchesCategory =
      !allowedCategories ||
      allowedCategories.length === 0 ||
      allowedCategories.includes(component.category);

    const notAlreadyAdded = !existingComponentTypes.includes(component.type);

    return matchesSearch && matchesType && matchesCategory && notAlreadyAdded;
  });

  const handleTemplateSelect = (componentType: ComponentType) => {
    if (componentType === 'compete-form') {
      setPickingCompeteForm(true);
      return;
    }
    onSelectTemplate(componentType);
    onClose();
  };

  const handlePlaceCompeteForm = (form: StudioCaptureForm) => {
    onPlaceCompeteForm?.(form);
    onClose();
  };

  const handleLibrarySelect = (libraryComponentId: string) => {
    onSelectLibraryComponent(libraryComponentId);
    onClose();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.component) return;

    try {
      setIsDeleting(true);
      await libraryApi.deleteComponent(deleteConfirm.component.id);
      setLibraryComponents(prev => prev.filter(c => c.id !== deleteConfirm.component!.id));
      setDeleteConfirm({ isOpen: false, component: null });
    } catch (error) {
      console.error('Failed to delete library component:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  // Group template components by category
  const templatesByCategory = filteredTemplateComponents.reduce(
    (acc, component) => {
      if (!acc[component.category]) {
        acc[component.category] = [];
      }
      acc[component.category].push(component);
      return acc;
    },
    {} as Record<string, ComponentOption[]>
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[70]">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Add Component</h2>
            <p className="text-sm text-gray-500 mt-1">
              {isLayoutMode
                ? 'Add navigation, footer, or content area components to this layout'
                : 'Choose from templates or your saved library components'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs and Search */}
        <div className="border-b border-gray-100 px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('templates')}
                className={cn(
                  'px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2',
                  activeTab === 'templates'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                )}
              >
                <Plus size={16} />
                <span>Templates</span>
              </button>
              <button
                onClick={() => setActiveTab('library')}
                className={cn(
                  'px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2',
                  activeTab === 'library'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                )}
              >
                <Bookmark size={16} />
                <span>Library</span>
                {libraryComponents.length > 0 && (
                  <span className="ml-2 bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                    {libraryComponents.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="flex space-x-4">
            <div className="flex-1 relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search components..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <select
              value={selectedType}
              onChange={e => setSelectedType(e.target.value as ComponentType | 'all')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Types</option>
              <option value="navbar">Navigation</option>
              <option value="hero">Hero</option>
              <option value="split-hero">Split Hero</option>
              <option value="simple-header">Simple Header</option>
              <option value="features">Features</option>
              <option value="cta">Call to Action</option>
              <option value="footer">Footer</option>
              <option value="form">Forms</option>
              <option value="team">Team</option>
              <option value="media-text">Media Text</option>
              <option value="pricing">Pricing</option>
              <option value="video">Video</option>
              <option value="text-blocks">Text Blocks</option>
              <option value="faq">FAQ</option>
              <option value="testimonials">Testimonials</option>
              <option value="countdown-timer">Countdown Timer</option>
              <option value="image-gallery">Image Gallery</option>
              <option value="image-info">Image Info</option>
              <option value="image-card">Image Cards</option>
              <option value="title-subtitle">Title Subtitle</option>
              <option value="contact-info">Contact Info</option>
              <option value="html-embed">HTML Embed</option>
              <option value="html-split">Split HTML</option>
              <option value="navigation-menu">Expandable Navigation Bar</option>
              <option value="logocloud">Logo Cloud</option>
              <option value="stats">Stats Counter</option>
              <option value="blog-preview">Blog Preview</option>
              <option value="conversion-popup">Conversion Popup</option>
            </select>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {pickingCompeteForm ? (
            <div className="max-w-lg mx-auto">
              <button
                type="button"
                onClick={() => setPickingCompeteForm(false)}
                className="text-sm text-gray-500 hover:text-gray-800 mb-4"
              >
                Back
              </button>
              <h3 className="text-base font-medium text-gray-900 mb-1">Choose a Studio form</h3>
              <p className="text-sm text-gray-500 mb-4">
                This form will be placed on the page. Submissions go to Compete Studio.
              </p>
              <CompeteFormPicker
                forms={studioForms}
                isLoading={studioFormsLoading}
                onSelect={handlePlaceCompeteForm}
              />
            </div>
          ) : activeTab === 'templates' ? (
            <TemplatesTab
              templatesByCategory={templatesByCategory}
              isLoading={isLoading}
              onSelectTemplate={handleTemplateSelect}
            />
          ) : (
            <LibraryTab
              isLoading={isLoadingLibrary}
              components={filteredLibraryComponents}
              searchTerm={searchTerm}
              selectedType={selectedType}
              currentUserId={getCurrentUserId()}
              onSelectComponent={handleLibrarySelect}
              onEditComponent={onEditLibraryComponent}
              onDeleteComponent={component => setDeleteConfirm({ isOpen: true, component })}
              isDeleting={isDeleting}
              isAddingToPage={isLoading}
            />
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-6 py-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-400">
              {activeTab === 'templates'
                ? 'Create components from scratch with customizable templates'
                : "Reuse components you've saved to your library"}
            </p>
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmDialog
        isOpen={deleteConfirm.isOpen}
        component={deleteConfirm.component}
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, component: null })}
      />
    </div>
  );
};

// Re-export utilities
export { componentOptions, getComponentIcon, getComponentColorScheme } from './componentOptions';
